import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { api } from '../../lib/api';
import { AuthLayout } from './AuthLayout';
import { useFormErrors } from './useFormErrors';

export function ForgotPasswordPage() {
  const errors = useFormErrors();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    errors.clear();
    setBusy(true);

    try {
      await api.post('/auth/forgot', { email }, { anonymous: true });
      setSent(true);
    } catch (error) {
      errors.capture(error);
    } finally {
      setBusy(false);
    }
  }

  /*
   * The same confirmation whether or not the address exists.
   *
   * The server is genuinely indistinguishable here - unlike registration,
   * nothing observable is created either way - and the wording has to hold that
   * up. "If there is an account" rather than "we sent it to you", because the
   * second would be a lie half the time and a disclosure the other half.
   */
  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        footer={<Link to="/login" className="text-accent hover:underline">Back to sign in</Link>}
      >
        <Alert tone="info">
          If there is an account for <strong className="text-ink">{email}</strong>, a reset link is on its way. It
          works for one hour.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link to choose a new one."
      footer={<Link to="/login" className="text-accent hover:underline">Back to sign in</Link>}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {errors.message && <Alert tone="error">{errors.message}</Alert>}

        <Field
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.fields.email}
        />

        <Button type="submit" loading={busy} full>
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
